/**
 * Moving across the theatre map (04 §2.2): which nodes are open, scouted or passed by, how
 * the numbers on the cards follow Enemy Readiness, and the POW Rescue node that an enemy
 * capture inserts and carries forward until it is attempted.
 */

import { Pcg32 } from '../sim/rng';
import { hazardRules } from './content/career';
import { MODIFIER_RULES } from './content/modifiers';
import { stampDogTeams } from './dogs';
import { blankNode } from './opgen/graph';
import { dressNode, enforceSky, extraModifier } from './opgen/dress';
import { findNode, type OperationState } from './state';
import { nodeThreat, rewardMultiplier } from './threat';
import type { TheatreNode } from './types';

const unresolved = (n: TheatreNode): boolean => n.state === 'hidden' || n.state === 'scouted' || n.state === 'open';

/** Take a Tag Recovery marker off a node: the mission was flown, or the company went another way (04 §2.2). */
export function clearTagMarker(op: OperationState, node: TheatreNode): void {
  delete node.tagRecovery;
  if (node.params) delete node.params.tagRecovery;
  delete op.tagNodes[node.id];
}

/** Nodes the company may move to now. */
export function openNodes(op: OperationState): TheatreNode[] {
  if (op.status !== 'active') return [];
  const ids = op.position === null
    ? op.nodes.filter((n) => n.theatre === 0 && n.column === 0).map((n) => n.id)
    : findNode(op, op.position).links;
  return op.nodes.filter((n) => ids.includes(n.id) && unresolved(n));
}

/** At readiness 6+ an open node may pick up one forced modifier - rolled once, shown before committing. */
function rollForcedModifier(op: OperationState, node: TheatreNode): void {
  if (!node.params || op.forcedRolled.includes(node.id) || op.readiness < MODIFIER_RULES.forcedAtReadiness) return;
  op.forcedRolled.push(node.id);
  const rng = new Pcg32(node.params.seed, 0x0f0c);
  if (!rng.chance(MODIFIER_RULES.forcedChance)) return;
  const forced = extraModifier(rng, node.params.archetype, node.params.modifiers, node.params.threat);
  if (!forced) return;
  node.params.modifiers.push(forced);
  enforceSky(node.params);
  node.rewardMultiplier = rewardMultiplier(node.params.modifiers, node.variant);
}

/**
 * Re-derive every unresolved node from where the company stands: next column `open`, the
 * one after `scouted` (archetype only), beyond that `hidden`, unreachable `skipped`. Threat
 * ratings are refreshed too, so the map never shows a stale number - and the dog teams with them (ROUND8).
 */
export function refreshNodes(op: OperationState): void {
  const depth = new Map<string, number>();
  let frontier = openNodes(op);
  for (let d = 0; frontier.length > 0; d++) {
    const next: TheatreNode[] = [];
    for (const node of frontier) {
      if (depth.has(node.id)) continue;
      depth.set(node.id, d);
      for (const id of node.links) next.push(findNode(op, id));
    }
    frontier = next.filter((n) => unresolved(n) && !depth.has(n.id));
  }
  for (const node of op.nodes) {
    if (!unresolved(node)) continue;
    const d = depth.get(node.id);
    if (d === undefined) {
      node.state = 'skipped';
      clearTagMarker(op, node);
    } else if (d === 0) node.state = 'open';
    else node.state = d === 1 || op.revealedTheatres.includes(node.theatre) ? 'scouted' : 'hidden';
    if (node.params) node.params.threat = nodeThreat(op, node);
    if (node.state === 'open') rollForcedModifier(op, node);
    if (node.params) stampDogTeams(node.params, op.difficulty);
  }
}

/** Add a Liberate-the-Camp node beside whatever `from` leads to (04 §2.2). */
export function insertRescue(op: OperationState, from: TheatreNode): void {
  const targets = from.links.map((id) => findNode(op, id)).filter(unresolved);
  const first = targets[0];
  const biome = first ? op.theatres[first.theatre] : undefined;
  if (!first || !biome) return;
  const rescue = blankNode(`t${first.theatre}c${first.column}x${op.nodes.length}`, first.theatre, first.column,
    op.nodes.filter((n) => n.theatre === first.theatre && n.column === first.column).length);
  rescue.kind = 'rescue';
  // Beside the keystone it leads into the keystone; otherwise onward wherever its neighbours lead.
  rescue.links = first.keystone ? [first.id] : [...new Set(targets.flatMap((n) => n.links))];
  const rng = new Pcg32(op.seed, 0x0905).fork(rescue.id);
  const ctx = {
    difficulty: op.difficulty, readiness: op.readiness, rules: hazardRules(op.hazard), theatres: op.theatres.length,
  };
  dressNode(rng, rescue, 'pow-camp', biome, ctx, []);
  from.links.push(rescue.id);
  op.nodes.push(rescue);
  op.rescueNodeId = rescue.id;
  op.log.push('A rescue is possible: a P.O.W. camp has been located.');
}

/**
 * Stand on `node`. An unattempted rescue is passed by and re-offered in the next column;
 * returns true when it can no longer be offered (the theatre's keystone is done).
 */
export function advanceTo(op: OperationState, node: TheatreNode, hasCaptives: boolean): boolean {
  op.position = node.id;
  let captivesLost = false;
  if (op.rescueNodeId !== null && op.rescueNodeId !== node.id) {
    const passed = findNode(op, op.rescueNodeId);
    if (unresolved(passed)) passed.state = 'skipped';
    op.rescueNodeId = null;
  }
  if (op.rescueNodeId === node.id) op.rescueNodeId = null;
  if (hasCaptives && op.rescueNodeId === null) {
    if (node.keystone) captivesLost = true;
    else insertRescue(op, node);
  }
  refreshNodes(op);
  return captivesLost;
}
